/**
 * Route Planner Store
 *
 * Enterprise-level route planning state management.
 * Features:
 * - Drag & drop reordering between routes and unassigned panel
 * - Google Directions API / Route Optimization API hybrid
 * - Configurable stoppage times (per chauffeur, per stop)
 * - Time window validation with visual warnings
 * - Capacity tracking with overflow warnings
 *
 * Praat via het office-serverpad /api/routes/* (kaart 10 fase 2a) — een
 * geauthenticeerde byte-compatibele proxy op InternalRouteRest.cls.
 */

import { create } from 'zustand';
import { salesforceClient } from '../lib/salesforce';
import type {
  RouteDetail,
  RouteStop,
  UnassignedWerkOrder,
  ChauffeurDto,
  Depot,
  OptimizeRequest,
  ReorderRequest,
  AddStopRequest,
} from '../lib/salesforce/types';

// ============================================================================
// CONSTANTS
// ============================================================================

const DEFAULT_STOPPAGE_TIME = 6; // minutes
const DEFAULT_DEPARTURE_TIME = '06:00';
const DEFAULT_DEPOT: Depot = {
  address: 'Oeveraseweg 13, 7971 PA Havelte',
  latitude: 52.7582089,
  longitude: 6.2362659,
};

// ============================================================================
// INTERFACES
// ============================================================================

interface StoppageTimeOverrides {
  [stopId: string]: number;
}

interface RoutePlannerState {
  // Current route
  routeId: string | null;
  route: RouteDetail | null;

  // Stops data
  stops: RouteStop[];
  depot: Depot | null;
  totalKilometers: number | null;
  totalJourneyTime: string | null;

  // Unassigned work orders (left panel)
  unassignedWerkOrders: UnassignedWerkOrder[];
  unassignedFilter: 'all' | 'ophalen' | 'afleveren';
  unassignedPostcodeFilter: string;
  showUnassignedOnMap: boolean;
  showOphalenOnMap: boolean;
  showAfleverenOnMap: boolean;

  // Chauffeurs
  chauffeurs: ChauffeurDto[];

  // Stoppage time settings
  defaultStoppageTime: number;
  stoppageTimeOverrides: StoppageTimeOverrides;
  departureTime: string; // HH:mm format

  // Route state
  isLocked: boolean;
  capacityRemaining: number | null;
  capacityTotal: number | null;
  // Ophalen/Afleveren capaciteit
  stopCountOphalen: number;
  stopCountAfleveren: number;
  capaciteitResterendOphalen: number | null;
  capaciteitOverrideOphalen: number | null;
  capaciteitResterendAfleveren: number | null;
  capaciteitOverrideAfleveren: number | null;
  ophalenNietVoorstellen: boolean;
  afleverenNietVoorstellen: boolean;

  // UI State
  isLoading: boolean;
  isLoadingUnassigned: boolean;
  isOptimizing: boolean;
  isSaving: boolean;
  isAddingStop: boolean;
  isRemovingStop: boolean;
  isMarkingNietGelukt: boolean;
  isMarkingTochGeslaagd: boolean;
  isFinalizing: boolean;
  lastFinalizeResult: { itemsUpdated: number; routeName: string } | null;
  error: string | null;
  hasUnsavedChanges: boolean;

  // Selected stop for details panel
  selectedStopId: string | null;

  // Multi-selection for cross-route move action
  selectedStopIds: Set<string>;

  // Stop-ids for the open "niet gelukt" dialog (null = closed). Serves both the
  // per-stop action (single id) and the bulk action (selection).
  nietGeluktStopIds: string[] | null;

  // Stop-ids for the open "toch geslaagd" dialog (null = closed) — de inverse:
  // een overgeslagen stop alsnog als geslaagd markeren.
  tochGeslaagdStopIds: string[] | null;

  // Google Maps state (for DirectionsRenderer)
  directionsResult: google.maps.DirectionsResult | null;
  mapCenter: { lat: number; lng: number };
  mapZoom: number;

  // Actions - Data Loading
  loadRoute: (routeId: string) => Promise<void>;
  loadUnassignedWerkOrders: () => Promise<void>;
  loadChauffeurs: () => Promise<void>;
  refreshRoute: () => Promise<void>;

  // Actions - Stop manipulation
  reorderStops: (fromIndex: number, toIndex: number) => void;
  addStopFromUnassigned: (werkOrder: UnassignedWerkOrder, targetIndex?: number) => Promise<void>;
  removeStop: (stopId: string, newStatus?: string) => Promise<void>;
  markStopsNietGelukt: (stopIds: string[], opts?: { notitie?: string; informeerKlant?: boolean }) => Promise<void>;
  markStopsTochGeslaagd: (stopIds: string[], opts?: { notitie?: string }) => Promise<void>;
  setSelectedStop: (stopId: string | null) => void;

  // Multi-select actions for cross-route move
  toggleStopSelection: (stopId: string) => void;
  clearStopSelection: () => void;

  // "Niet gelukt" dialog open/close
  openNietGeluktDialog: (stopIds: string[]) => void;
  closeNietGeluktDialog: () => void;

  // "Toch geslaagd" dialog open/close
  openTochGeslaagdDialog: (stopIds: string[]) => void;
  closeTochGeslaagdDialog: () => void;

  // Actions - Stop geocoding
  regeocodeStop: (stopId: string) => Promise<void>;
  isRegeocoding: boolean;
  geocodeMissingStops: () => Promise<void>;
  isGeocodingMissing: boolean;

  // Actions - Stop time window
  updateStopTimeWindow: (stopId: string, timeFrom: string | null, timeTo: string | null) => Promise<void>;

  // Actions - Stop bijrijder (handmatige vlag)
  setStopBijrijderNodig: (stopId: string, value: boolean) => Promise<void>;

  // Actions - Stoppage time
  setDefaultStoppageTime: (minutes: number) => void;
  setStoppageTimeOverride: (stopId: string, minutes: number) => void;
  clearStoppageTimeOverride: (stopId: string) => void;

  // Actions - Route actions
  optimizeRoute: () => Promise<void>;
  optimizeFromStop: (stopIndex: number) => Promise<void>;
  recalculateTimes: () => Promise<void>;
  lockRoute: () => Promise<void>;
  unlockRoute: () => Promise<void>;
  finalizeRoute: () => Promise<void>;
  clearLastFinalizeResult: () => void;
  updateChauffeur: (chauffeurId: string) => Promise<void>;
  updateCapacityOverride: (field: 'ophalen' | 'afleveren', value: number | null) => Promise<void>;
  updateNietVoorstellen: (field: 'ophalen' | 'afleveren', value: boolean) => Promise<void>;
  setBijrijder: (input: { bijrijderId: string | null; bijrijderNodig: boolean }) => Promise<void>;

  // Actions - Filters
  setUnassignedFilter: (filter: 'all' | 'ophalen' | 'afleveren') => void;
  setUnassignedPostcodeFilter: (postcode: string) => void;
  setDepartureTime: (time: string) => void;
  toggleShowUnassignedOnMap: () => void;
  toggleShowOphalenOnMap: () => void;
  toggleShowAfleverenOnMap: () => void;

  // Actions - Depot
  setDepot: (depot: Depot) => void;
  resetDepotToDefault: () => void;

  // Actions - Map
  setDirectionsResult: (result: google.maps.DirectionsResult | null) => void;
  setMapCenter: (center: { lat: number; lng: number }) => void;
  setMapZoom: (zoom: number) => void;

  // Error handling
  clearError: () => void;

  // Cleanup
  reset: () => void;
}

// ============================================================================
// STORE
// ============================================================================

export const useRoutePlannerStore = create<RoutePlannerState>((set, get) => ({
  // Initial state
  routeId: null,
  route: null,
  stops: [],
  depot: null,
  totalKilometers: null,
  totalJourneyTime: null,
  unassignedWerkOrders: [],
  unassignedFilter: 'all',
  unassignedPostcodeFilter: '',
  showUnassignedOnMap: false,
  showOphalenOnMap: true,
  showAfleverenOnMap: true,
  chauffeurs: [],
  defaultStoppageTime: DEFAULT_STOPPAGE_TIME,
  stoppageTimeOverrides: {},
  departureTime: DEFAULT_DEPARTURE_TIME,
  isLocked: false,
  capacityRemaining: null,
  capacityTotal: null,
  stopCountOphalen: 0,
  stopCountAfleveren: 0,
  capaciteitResterendOphalen: null,
  capaciteitOverrideOphalen: null,
  capaciteitResterendAfleveren: null,
  capaciteitOverrideAfleveren: null,
  ophalenNietVoorstellen: false,
  afleverenNietVoorstellen: false,
  isLoading: false,
  isLoadingUnassigned: false,
  isOptimizing: false,
  isSaving: false,
  isAddingStop: false,
  isRemovingStop: false,
  isMarkingNietGelukt: false,
  isMarkingTochGeslaagd: false,
  isRegeocoding: false,
  isGeocodingMissing: false,
  isFinalizing: false,
  lastFinalizeResult: null,
  error: null,
  hasUnsavedChanges: false,
  selectedStopId: null,
  selectedStopIds: new Set<string>(),
  nietGeluktStopIds: null,
  tochGeslaagdStopIds: null,
  directionsResult: null,
  mapCenter: { lat: 52.5, lng: 5.3 }, // Center of Netherlands (better centered)
  mapZoom: 7, // Slightly zoomed out to show more of NL

  // ==================== DATA LOADING ====================

  loadRoute: async (routeId: string) => {
    set({ isLoading: true, error: null, routeId });

    try {
      const route = await salesforceClient.getRoute(routeId);

      // Extract stoppage time overrides from stops
      const overrides: StoppageTimeOverrides = {};
      route.stops.forEach(stop => {
        if (stop.stoppageTime !== route.defaultStoppageTime) {
          overrides[stop.id] = stop.stoppageTime;
        }
      });

      // Extract departure time from startDateTimePlanned (e.g. "2026-03-04T06:30:00Z" → "06:30")
      let loadedDepartureTime = DEFAULT_DEPARTURE_TIME;
      if (route.startDateTimePlanned) {
        try {
          const dt = new Date(route.startDateTimePlanned);
          const hh = dt.getUTCHours().toString().padStart(2, '0');
          const mm = dt.getUTCMinutes().toString().padStart(2, '0');
          loadedDepartureTime = `${hh}:${mm}`;
        } catch {
          // Fall back to default
        }
      }

      set({
        route,
        stops: route.stops || [],
        depot: route.depot,
        totalKilometers: route.totalKilometers,
        totalJourneyTime: route.hourAndMinutes,
        isLocked: route.locked,
        capacityRemaining: route.capaciteitResterend ?? null,
        capacityTotal: route.capaciteitTotaal ?? null,
        // Count stops locally as primary source (API fields may not be deployed yet)
        stopCountOphalen: route.stopCountOphalen ?? (route.stops || []).filter(s => s.stopType === 'Ophalen').length,
        stopCountAfleveren: route.stopCountAfleveren ?? (route.stops || []).filter(s => s.stopType === 'Terugbrengen').length,
        capaciteitResterendOphalen: route.capaciteitResterendOphalen ?? null,
        capaciteitOverrideOphalen: route.capaciteitOverrideOphalen ?? null,
        capaciteitResterendAfleveren: route.capaciteitResterendAfleveren ?? null,
        capaciteitOverrideAfleveren: route.capaciteitOverrideAfleveren ?? null,
        ophalenNietVoorstellen: route.ophalenNietVoorstellen ?? false,
        afleverenNietVoorstellen: route.afleverenNietVoorstellen ?? false,
        defaultStoppageTime: route.defaultStoppageTime || DEFAULT_STOPPAGE_TIME,
        stoppageTimeOverrides: overrides,
        departureTime: loadedDepartureTime,
        isLoading: false,
        hasUnsavedChanges: false,
        selectedStopIds: new Set<string>(),
      });

      // Auto-recalculate times if there are stops but no calculated arrival times yet
      const hasStopsWithoutTimes = route.stops?.some(stop => !stop.arrivalTime);
      if (route.stops && route.stops.length > 0 && hasStopsWithoutTimes && !route.locked) {
        // Defer recalculation to avoid blocking the initial load
        setTimeout(() => {
          get().recalculateTimes();
        }, 100);
      }

      // Also load unassigned work orders - only if not locked
      if (!route.locked) {
        get().loadUnassignedWerkOrders();
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load route',
        isLoading: false,
      });
    }
  },

  loadUnassignedWerkOrders: async () => {
    const { unassignedFilter, unassignedPostcodeFilter } = get();
    set({ isLoadingUnassigned: true });

    try {
      const type = unassignedFilter === 'all' ? undefined : unassignedFilter;
      const postcode = unassignedPostcodeFilter || undefined;

      const werkOrders = await salesforceClient.getUnassignedWerkOrders(type, postcode);
      set({ unassignedWerkOrders: werkOrders, isLoadingUnassigned: false });
    } catch (error) {
      console.error('Failed to load unassigned werk orders:', error);
      set({ isLoadingUnassigned: false });
    }
  },

  loadChauffeurs: async () => {
    try {
      const chauffeurs = await salesforceClient.getChauffeurs();
      set({ chauffeurs });
    } catch (error) {
      console.error('Failed to load chauffeurs:', error);
    }
  },

  refreshRoute: async () => {
    const { routeId } = get();
    if (routeId) {
      await get().loadRoute(routeId);
    }
  },

  // ==================== STOP MANIPULATION ====================

  reorderStops: (fromIndex: number, toIndex: number) => {
    const { stops, isLocked } = get();
    const stopToMove = stops[fromIndex];

    // Block if route is locked AND the stop being moved was NOT added after lock
    if (isLocked && stopToMove && !stopToMove.isAddedAfterLock) return;

    const newStops = [...stops];
    const [removed] = newStops.splice(fromIndex, 1);
    if (removed) {
      newStops.splice(toIndex, 0, removed);
      // Update routeSequence for all stops
      newStops.forEach((stop, index) => {
        stop.routeSequence = index + 1;
      });
    }
    set({ stops: newStops, hasUnsavedChanges: true });
  },

  addStopFromUnassigned: async (werkOrder: UnassignedWerkOrder, _targetIndex?: number) => {
    const { routeId } = get();
    if (!routeId) return;
    // Note: We allow adding stops to locked routes - they get Added_After_Lock flag

    set({ isAddingStop: true, error: null });

    try {
      const request: AddStopRequest = {
        werkOrderId: werkOrder.id,
        type: werkOrder.type,
        stoppageTime: get().defaultStoppageTime,
        // TODO: Use _targetIndex for position when API supports it
      };

      const updatedRoute = await salesforceClient.addStop(routeId, request);

      // Remove from unassigned list
      const { unassignedWerkOrders } = get();
      const filteredUnassigned = unassignedWerkOrders.filter(wo => wo.id !== werkOrder.id);

      set({
        route: updatedRoute,
        stops: updatedRoute.stops,
        unassignedWerkOrders: filteredUnassigned,
        capacityRemaining: updatedRoute.capaciteitResterend,
        isAddingStop: false,
        hasUnsavedChanges: true,
      });

      // De nieuwe stop heeft nog geen aankomsttijd en alle stops erachter
      // dragen nog de tijden van vóór de toevoeging — addStop in Apex roept
      // recalculateRouteTimesAndDistances niet aan, anders dan reorder en
      // optimize. Meteen herberekenen, ook op een gelockte route (besluit
      // Willem 24-08-2026). De cron in api/cron/stop-inpasser.ts is het
      // vangnet voor stops die niet via de planner binnenkomen.
      await get().recalculateTimes();
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to add stop',
        isAddingStop: false,
      });
    }
  },

  removeStop: async (stopId: string, newStatus?: string) => {
    const { routeId, isLocked, stops } = get();
    if (!routeId) return;

    // Check if stop was added after lock - only these can be removed on locked routes
    const stopToRemove = stops.find(s => s.id === stopId);
    if (isLocked && stopToRemove && !stopToRemove.isAddedAfterLock) return;

    set({ isRemovingStop: true, error: null });

    try {
      const updatedRoute = await salesforceClient.removeStop(routeId, stopId, newStatus);

      set({
        route: updatedRoute,
        stops: updatedRoute.stops,
        capacityRemaining: updatedRoute.capaciteitResterend,
        isRemovingStop: false,
        hasUnsavedChanges: true,
      });

      // Refresh unassigned list to include removed item
      get().loadUnassignedWerkOrders();
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to remove stop',
        isRemovingStop: false,
      });
    }
  },

  markStopsNietGelukt: async (stopIds: string[], opts?: { notitie?: string; informeerKlant?: boolean }) => {
    const { routeId } = get();
    if (!routeId || stopIds.length === 0) return;

    set({ isMarkingNietGelukt: true, error: null });

    try {
      const updatedRoute = await salesforceClient.markStopsNietGelukt(routeId, stopIds, opts);

      set({
        route: updatedRoute,
        stops: updatedRoute.stops,
        capacityRemaining: updatedRoute.capaciteitResterend,
        isMarkingNietGelukt: false,
        hasUnsavedChanges: true,
      });

      // Selectie wissen + unassigned-lijst verversen (WO's staan nu weer op '...plannen')
      get().clearStopSelection();
      get().loadUnassignedWerkOrders();
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Stops markeren als niet gelukt mislukt',
        isMarkingNietGelukt: false,
      });
    }
  },

  markStopsTochGeslaagd: async (stopIds: string[], opts?: { notitie?: string }) => {
    const { routeId } = get();
    if (!routeId || stopIds.length === 0) return;

    set({ isMarkingTochGeslaagd: true, error: null });

    try {
      const updatedRoute = await salesforceClient.markStopsTochGeslaagd(routeId, stopIds, opts);

      set({
        route: updatedRoute,
        stops: updatedRoute.stops,
        capacityRemaining: updatedRoute.capaciteitResterend,
        isMarkingTochGeslaagd: false,
        hasUnsavedChanges: true,
      });

      // Selectie wissen + unassigned-lijst verversen (WO's zijn nu Opgehaald/Teruggebracht)
      get().clearStopSelection();
      get().loadUnassignedWerkOrders();
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Stop als geslaagd markeren mislukt',
        isMarkingTochGeslaagd: false,
      });
    }
  },

  setSelectedStop: (stopId: string | null) => {
    set({ selectedStopId: stopId });
  },

  toggleStopSelection: (stopId: string) => {
    set((state) => {
      const next = new Set(state.selectedStopIds);
      if (next.has(stopId)) {
        next.delete(stopId);
      } else {
        next.add(stopId);
      }
      return { selectedStopIds: next };
    });
  },

  openNietGeluktDialog: (stopIds: string[]) => {
    set({ nietGeluktStopIds: stopIds });
  },

  closeNietGeluktDialog: () => {
    set({ nietGeluktStopIds: null });
  },

  openTochGeslaagdDialog: (stopIds: string[]) => {
    set({ tochGeslaagdStopIds: stopIds });
  },

  closeTochGeslaagdDialog: () => {
    set({ tochGeslaagdStopIds: null });
  },

  clearStopSelection: () => {
    set({ selectedStopIds: new Set<string>() });
  },

  // ==================== STOP GEOCODING ====================

  regeocodeStop: async (stopId: string) => {
    const { routeId } = get();
    if (!routeId) return;

    set({ isRegeocoding: true, error: null });

    try {
      const updatedRoute = await salesforceClient.regeocodeStop(routeId, stopId);

      set({
        route: updatedRoute,
        stops: updatedRoute.stops,
        isRegeocoding: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to re-geocode stop',
        isRegeocoding: false,
      });
    }
  },

  geocodeMissingStops: async () => {
    const { routeId } = get();
    if (!routeId) return;

    set({ isGeocodingMissing: true, error: null });

    try {
      const updatedRoute = await salesforceClient.geocodeMissingStops(routeId);

      set({
        route: updatedRoute,
        stops: updatedRoute.stops,
        isGeocodingMissing: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Geocoding ontbrekende stops mislukt',
        isGeocodingMissing: false,
      });
    }
  },

  // ==================== STOP TIME WINDOW ====================

  updateStopTimeWindow: async (stopId: string, timeFrom: string | null, timeTo: string | null) => {
    const { routeId, stops } = get();
    if (!routeId) return;

    // Save previous state for rollback
    const previousStops = stops;

    // Optimistic update
    const newStops = stops.map(stop =>
      stop.id === stopId ? { ...stop, timeFrom, timeTo } : stop
    );
    set({ stops: newStops, error: null });

    try {
      const updatedRoute = await salesforceClient.updateStopTimeWindow(routeId, stopId, {
        timeFrom: timeFrom || null,
        timeTo: timeTo || null,
      });

      set({
        route: updatedRoute,
        stops: updatedRoute.stops,
      });
    } catch (error) {
      // Rollback
      set({
        stops: previousStops,
        error: error instanceof Error ? error.message : 'Failed to update time window',
      });
    }
  },

  // ==================== STOP BIJRIJDER (handmatige vlag) ====================

  setStopBijrijderNodig: async (stopId: string, value: boolean) => {
    const { routeId, stops } = get();
    if (!routeId) return;

    // Save previous state for rollback
    const previousStops = stops;

    // Optimistic update — alleen de handmatige vlag; de effectieve waarde komt
    // uit de backend-respons (kan ook uit een toeslag volgen).
    const newStops = stops.map(stop =>
      stop.id === stopId
        ? { ...stop, bijrijderNodigHandmatig: value, bijrijderNodig: value || stop.bijrijderBron === 'toeslag' }
        : stop
    );
    set({ stops: newStops, error: null });

    try {
      const updatedRoute = await salesforceClient.setStopBijrijderNodig(routeId, stopId, value);
      set({
        route: updatedRoute,
        stops: updatedRoute.stops,
      });
    } catch (error) {
      // Rollback
      set({
        stops: previousStops,
        error: error instanceof Error ? error.message : 'Failed to update bijrijder flag',
      });
    }
  },

  // ==================== STOPPAGE TIME ====================

  setDefaultStoppageTime: (minutes: number) => {
    set({ defaultStoppageTime: minutes, hasUnsavedChanges: true });
  },

  setStoppageTimeOverride: (stopId: string, minutes: number) => {
    const { stoppageTimeOverrides, stops } = get();
    const newOverrides = { ...stoppageTimeOverrides, [stopId]: minutes };

    // Also update local stop state
    const newStops = stops.map(stop =>
      stop.id === stopId ? { ...stop, stoppageTime: minutes } : stop
    );

    set({
      stoppageTimeOverrides: newOverrides,
      stops: newStops,
      hasUnsavedChanges: true
    });
  },

  clearStoppageTimeOverride: (stopId: string) => {
    const { stoppageTimeOverrides, stops, defaultStoppageTime } = get();
    const newOverrides = { ...stoppageTimeOverrides };
    delete newOverrides[stopId];

    // Reset stop to default
    const newStops = stops.map(stop =>
      stop.id === stopId ? { ...stop, stoppageTime: defaultStoppageTime } : stop
    );

    set({
      stoppageTimeOverrides: newOverrides,
      stops: newStops,
      hasUnsavedChanges: true
    });
  },

  // ==================== ROUTE ACTIONS ====================

  optimizeRoute: async () => {
    const { routeId, defaultStoppageTime, stoppageTimeOverrides, departureTime, route } = get();
    if (!routeId) return;

    set({ isOptimizing: true, error: null });

    try {
      // Build departure datetime from route date + time
      let departureDateTime: string | undefined;
      if (route?.datum && departureTime) {
        departureDateTime = `${route.datum}T${departureTime}:00`;
      }

      const request: OptimizeRequest = {
        defaultStoppageTime,
        stoppageTimeOverrides,
        departureTime: departureDateTime,
      };

      const updatedRoute = await salesforceClient.optimizeRoute(routeId, request);

      set({
        route: updatedRoute,
        stops: updatedRoute.stops,
        totalKilometers: updatedRoute.totalKilometers,
        totalJourneyTime: updatedRoute.hourAndMinutes,
        isOptimizing: false,
        hasUnsavedChanges: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to optimize route',
        isOptimizing: false,
      });
    }
  },

  optimizeFromStop: async (stopIndex: number) => {
    const { routeId, route, defaultStoppageTime, stoppageTimeOverrides, departureTime } = get();
    if (!routeId) return;

    set({ isOptimizing: true, error: null });

    try {
      // Build departure datetime from route date + time
      let departureDateTime: string | undefined;
      if (route?.datum && departureTime) {
        departureDateTime = `${route.datum}T${departureTime}:00`;
      }

      const request: OptimizeRequest = {
        defaultStoppageTime,
        stoppageTimeOverrides,
        departureTime: departureDateTime,
        optimizeFromIndex: stopIndex,
      };

      const updatedRoute = await salesforceClient.optimizeRoute(routeId, request);

      set({
        route: updatedRoute,
        stops: updatedRoute.stops,
        totalKilometers: updatedRoute.totalKilometers,
        totalJourneyTime: updatedRoute.hourAndMinutes,
        isOptimizing: false,
        hasUnsavedChanges: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to optimize route',
        isOptimizing: false,
      });
    }
  },

  recalculateTimes: async () => {
    const { routeId, stops, defaultStoppageTime, stoppageTimeOverrides, departureTime, route } = get();
    if (!routeId) return;

    set({ isSaving: true, error: null });

    try {
      // Build departure datetime from route date + time
      let departureDateTime: string | undefined;
      if (route?.datum && departureTime) {
        departureDateTime = `${route.datum}T${departureTime}:00`;
      }

      const request: ReorderRequest = {
        stopIds: stops.map(s => s.id),
        defaultStoppageTime,
        stoppageTimeOverrides,
        departureTime: departureDateTime,
      };

      const updatedRoute = await salesforceClient.reorderStops(routeId, request);

      set({
        route: updatedRoute,
        stops: updatedRoute.stops,
        totalKilometers: updatedRoute.totalKilometers,
        totalJourneyTime: updatedRoute.hourAndMinutes,
        isSaving: false,
        hasUnsavedChanges: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to recalculate times',
        isSaving: false,
      });
    }
  },

  lockRoute: async () => {
    const { routeId, hasUnsavedChanges } = get();
    if (!routeId) return;

    // Save changes first if needed
    if (hasUnsavedChanges) {
      await get().recalculateTimes();
    }

    set({ isSaving: true, error: null });

    try {
      const result = await salesforceClient.setRouteLocked(routeId, true);
      set({ isLocked: result.locked, isSaving: false });
      // Route-failover Laag 1: het lock-endpoint (/api/routes/{id}/lock)
      // ververst het failover-rijblad server-side ná de response — sinds
      // kaart 10 fase 2a niet meer vanuit de browser (betrouwbaarder; de
      // 05:30-ochtend-cron blijft het vangnet).
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to lock route',
        isSaving: false,
      });
    }
  },

  unlockRoute: async () => {
    const { routeId } = get();
    if (!routeId) return;

    set({ isSaving: true, error: null });

    try {
      const result = await salesforceClient.setRouteLocked(routeId, false);
      set({ isLocked: result.locked, isSaving: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to unlock route',
        isSaving: false,
      });
    }
  },

  finalizeRoute: async () => {
    const { routeId, route } = get();
    if (!routeId) return;

    set({ isFinalizing: true, error: null });

    try {
      const result = await salesforceClient.finalizeRoute(routeId);
      set({
        isFinalizing: false,
        lastFinalizeResult: { itemsUpdated: result.itemsUpdated, routeName: result.routeName },
        route: route ? { ...route, status: 'Afgerond' } : route,
      });
    } catch (error) {
      set({
        isFinalizing: false,
        error: error instanceof Error ? error.message : 'Route afronden mislukt',
      });
    }
  },

  clearLastFinalizeResult: () => {
    set({ lastFinalizeResult: null });
  },

  updateChauffeur: async (chauffeurId: string) => {
    const { routeId } = get();
    if (!routeId) return;

    set({ isSaving: true, error: null });

    try {
      const updatedRoute = await salesforceClient.updateRoute(routeId, { chauffeurId });
      set({
        route: updatedRoute,
        isSaving: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update chauffeur',
        isSaving: false,
      });
    }
  },

  updateCapacityOverride: async (field: 'ophalen' | 'afleveren', value: number | null) => {
    const { routeId } = get();
    if (!routeId) return;

    set({ isSaving: true, error: null });

    try {
      const updatePayload = field === 'ophalen'
        ? { capaciteitOverrideOphalen: value }
        : { capaciteitOverrideAfleveren: value };

      const updatedRoute = await salesforceClient.updateRoute(routeId, updatePayload);
      const updatedStops = updatedRoute.stops || get().stops;
      set({
        route: updatedRoute,
        capacityRemaining: updatedRoute.capaciteitResterend ?? null,
        capacityTotal: updatedRoute.capaciteitTotaal ?? null,
        stopCountOphalen: updatedRoute.stopCountOphalen ?? updatedStops.filter(s => s.stopType === 'Ophalen').length,
        stopCountAfleveren: updatedRoute.stopCountAfleveren ?? updatedStops.filter(s => s.stopType === 'Terugbrengen').length,
        capaciteitResterendOphalen: updatedRoute.capaciteitResterendOphalen ?? null,
        capaciteitOverrideOphalen: updatedRoute.capaciteitOverrideOphalen ?? null,
        capaciteitResterendAfleveren: updatedRoute.capaciteitResterendAfleveren ?? null,
        capaciteitOverrideAfleveren: updatedRoute.capaciteitOverrideAfleveren ?? null,
        ophalenNietVoorstellen: updatedRoute.ophalenNietVoorstellen ?? false,
        afleverenNietVoorstellen: updatedRoute.afleverenNietVoorstellen ?? false,
        isSaving: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update capacity override',
        isSaving: false,
      });
    }
  },

  setBijrijder: async ({ bijrijderId, bijrijderNodig }) => {
    const { routeId } = get();
    if (!routeId) return;

    set({ isSaving: true, error: null });

    try {
      const updatedRoute = await salesforceClient.updateRoute(routeId, {
        bijrijderId,
        bijrijderNodig,
      });
      set({
        route: updatedRoute,
        isSaving: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update bijrijder',
        isSaving: false,
      });
    }
  },

  updateNietVoorstellen: async (field: 'ophalen' | 'afleveren', value: boolean) => {
    const { routeId } = get();
    if (!routeId) return;

    set({ isSaving: true, error: null });

    try {
      const updatePayload = field === 'ophalen'
        ? { ophalenNietVoorstellen: value }
        : { afleverenNietVoorstellen: value };

      const updatedRoute = await salesforceClient.updateRoute(routeId, updatePayload);
      set({
        route: updatedRoute,
        ophalenNietVoorstellen: updatedRoute.ophalenNietVoorstellen ?? false,
        afleverenNietVoorstellen: updatedRoute.afleverenNietVoorstellen ?? false,
        isSaving: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update niet-voorstellen',
        isSaving: false,
      });
    }
  },

  // ==================== FILTERS ====================

  setUnassignedFilter: (filter: 'all' | 'ophalen' | 'afleveren') => {
    set({ unassignedFilter: filter });
    get().loadUnassignedWerkOrders();
  },

  setUnassignedPostcodeFilter: (postcode: string) => {
    set({ unassignedPostcodeFilter: postcode });
    get().loadUnassignedWerkOrders();
  },

  setDepartureTime: (time: string) => {
    set({ departureTime: time, hasUnsavedChanges: true });
  },

  toggleShowUnassignedOnMap: () => {
    set((state) => ({ showUnassignedOnMap: !state.showUnassignedOnMap }));
  },

  toggleShowOphalenOnMap: () => {
    set((state) => ({ showOphalenOnMap: !state.showOphalenOnMap }));
  },

  toggleShowAfleverenOnMap: () => {
    set((state) => ({ showAfleverenOnMap: !state.showAfleverenOnMap }));
  },

  // ==================== MAP ====================

  setDirectionsResult: (result: google.maps.DirectionsResult | null) => {
    set({ directionsResult: result });
  },

  setMapCenter: (center: { lat: number; lng: number }) => {
    set({ mapCenter: center });
  },

  setMapZoom: (zoom: number) => {
    set({ mapZoom: zoom });
  },

  // ==================== DEPOT ====================

  setDepot: (depot: Depot) => {
    set({ depot });
  },

  resetDepotToDefault: () => {
    set({ depot: DEFAULT_DEPOT });
  },

  // ==================== CLEANUP ====================

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set({
      routeId: null,
      route: null,
      stops: [],
      depot: null,
      totalKilometers: null,
      totalJourneyTime: null,
      unassignedWerkOrders: [],
      unassignedFilter: 'all',
      unassignedPostcodeFilter: '',
      defaultStoppageTime: DEFAULT_STOPPAGE_TIME,
      stoppageTimeOverrides: {},
      departureTime: DEFAULT_DEPARTURE_TIME,
      isLocked: false,
      capacityRemaining: null,
      capacityTotal: null,
      stopCountOphalen: 0,
      stopCountAfleveren: 0,
      capaciteitResterendOphalen: null,
      capaciteitOverrideOphalen: null,
      capaciteitResterendAfleveren: null,
      capaciteitOverrideAfleveren: null,
      ophalenNietVoorstellen: false,
      afleverenNietVoorstellen: false,
      isLoading: false,
      isLoadingUnassigned: false,
      isOptimizing: false,
      isSaving: false,
      isAddingStop: false,
      isRemovingStop: false,
      isMarkingNietGelukt: false,
      isMarkingTochGeslaagd: false,
      isRegeocoding: false,
      isFinalizing: false,
      lastFinalizeResult: null,
      error: null,
      hasUnsavedChanges: false,
      selectedStopId: null,
      selectedStopIds: new Set<string>(),
      nietGeluktStopIds: null,
      tochGeslaagdStopIds: null,
      directionsResult: null,
      mapCenter: { lat: 52.7558, lng: 6.3969 },
      mapZoom: 9,
    });
  },
}));

// ============================================================================
// SELECTORS
// ============================================================================

/**
 * Get count of stops that have time window violations
 */
export const useTimeWindowViolations = () => {
  return useRoutePlannerStore(state =>
    state.stops.filter(stop => !stop.isWithinTimeWindow).length
  );
};

/**
 * Check if capacity is exceeded
 */
export const useCapacityWarning = () => {
  return useRoutePlannerStore(state =>
    state.capacityRemaining !== null && state.capacityRemaining <= 0
  );
};

/**
 * Get count of stops with coordinates (for map)
 */
export const useStopCoordinatesCount = () => {
  return useRoutePlannerStore(state =>
    state.stops.filter(s => s.latitude !== null && s.longitude !== null).length
  );
};
